import { useEffect, useRef, useState } from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { buildReturnUrl } from "../utils/stripeCheckoutSession";

/**
 * Stripe Payment Element — verify payment succeeded, then parent creates order.
 */
const StripeCheckoutForm = ({
  amountInCents,
  onValidateBilling,
  onPersistCheckoutSnapshot,
  onPaymentSuccess,
  onRecreatePaymentIntent,
  disabled = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const errorRef = useRef(null);

  useEffect(() => {
    if (errorMessage && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [errorMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || disabled) return;

    const billingOk = onValidateBilling?.();
    if (billingOk === false) return;

    setIsProcessing(true);
    setErrorMessage(null);

    if (onRecreatePaymentIntent) {
      try {
        await onRecreatePaymentIntent(amountInCents);
        await new Promise((r) => setTimeout(r, 500));
      } catch {
        setErrorMessage("Failed to update payment amount. Please try again.");
        setIsProcessing(false);
        return;
      }
    }

    onPersistCheckoutSnapshot?.();

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: buildReturnUrl(),
        },
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(error.message || "Payment failed");
        setIsProcessing(false);
        return;
      }

      // Only create order after Stripe reports succeeded (no redirect path)
      if (!paymentIntent?.id || paymentIntent.status !== "succeeded") {
        setErrorMessage(
          paymentIntent?.status
            ? `Payment status: ${paymentIntent.status}. Please try again.`
            : "Payment confirmation failed."
        );
        setIsProcessing(false);
        return;
      }

      await onPaymentSuccess(
        paymentIntent.id,
        paymentIntent.client_secret
      );
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
      setIsProcessing(false);
    }
  };

  const displayAmount = (amountInCents / 100).toFixed(2);

  return (
    <form onSubmit={handleSubmit} className="stripe-checkout-form">
      <PaymentElement
        options={{
          layout: {
            type: "accordion",
            defaultCollapsed: false,
            radios: true,
            spacedAccordionItems: true,
          },
          wallets: {
            applePay: "auto",
            googlePay: "auto",
            link: "auto",
          },
          paymentMethodOrder: [
            "card",
            "google_pay",
            "apple_pay",
            "amazon_pay",
            "klarna",
            "link",
          ],
        }}
      />

      {errorMessage && (
        <div ref={errorRef} className="stripe-checkout-error">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        className="apply-button stripe-checkout-submit"
        disabled={!stripe || isProcessing || disabled}
        style={{ width: "100%", marginTop: "16px" }}
      >
        {isProcessing ? "Processing..." : `CHECKOUT NOW — $${displayAmount}`}
      </button>
    </form>
  );
};

export default StripeCheckoutForm;
